import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {mapiqApi, ILocalStorage} from '@lib/infrastructure';
import {storageKeys, withPayloadType} from '@lib/utils';
import {normalize} from 'normalizr';
import {AppThunkApiConfig} from '../../createStore';
import {userEntity} from '../../data/entities';
import {NormalizedUserResult, NormalizedUserResultWithToken} from './types';

export const silentSignIn = createAsyncThunk<NormalizedUserResultWithToken, void, AppThunkApiConfig>(
  'auth/silentSignIn',
  async (_, {extra, rejectWithValue}) => {
    const {authManager, apiClient} = extra;
    const accessToken = await authManager.getAccessToken();

    if (!accessToken) return rejectWithValue('Failed getting the access token');

    const api = mapiqApi(apiClient);
    const response = await api.postUserLogin();
    const {entities} = normalize<any, NormalizedUserResult>(response, userEntity);
    return {
      accessProfiles: {...entities.accessProfiles},
      locationProfiles: {...entities.locationProfiles},
      parkingLocationProfiles: {...entities.parkingLocationProfiles},
      parkingQuotaProfiles: {...entities.parkingQuotaProfiles},
      users: {...entities.users},
      accessToken,
    };
  },
  {
    condition: (_, {getState}) => {
      const isSignedIn = getState().auth.isSignedIn;
      return isSignedIn === false;
    },
  },
);

export const signInPopUp = createAsyncThunk<NormalizedUserResultWithToken, void, AppThunkApiConfig>(
  'auth/signIn',
  async (_, {extra, rejectWithValue}) => {
    const {authManager, apiClient} = extra;

    const {accessToken} = await authManager.signInPopup!();
    if (!accessToken) return rejectWithValue('Failed signing user in');

    const api = mapiqApi(apiClient);
    const response = await api.postUserLogin();
    const {entities} = normalize<any, NormalizedUserResult>(response, userEntity);
    return {
      accessProfiles: {...entities.accessProfiles},
      locationProfiles: {...entities.locationProfiles},
      parkingLocationProfiles: {...entities.parkingLocationProfiles},
      parkingQuotaProfiles: {...entities.parkingQuotaProfiles},
      users: {...entities.users},
      accessToken,
    };
  },
  {
    condition: (_, {getState}) => {
      const isSignedIn = getState().auth.isSignedIn;
      return isSignedIn === false;
    },
  },
);

export const signInRedirect = createAsyncThunk<void, void, AppThunkApiConfig>(
  'auth/signInRedirect',
  async (_, {extra}) => {
    const {authManager} = extra;
    authManager.signInRedirect!();
  },
  {
    condition: (_, {getState}) => {
      const isSignedIn = getState().auth.isSignedIn;
      return isSignedIn === false;
    },
  },
);

export const forceSignInRedirect = createAsyncThunk<void, void, AppThunkApiConfig>(
  'auth/signInRedirectForced',
  async (_, {extra}) => {
    const {authManager} = extra;
    authManager.signInRedirect!();
  },
);

export const updateUserCredentials = createAsyncThunk<
  NormalizedUserResultWithToken,
  {accessToken: string},
  AppThunkApiConfig
>('auth/updateUserCredentials', async ({accessToken}, {extra}) => {
  const {apiClient} = extra;
  const api = mapiqApi(apiClient);

  const response = await api.postUserLogin();
  const {entities} = normalize<any, NormalizedUserResult>(response, userEntity);

  return {
    accessProfiles: {...entities.accessProfiles},
    locationProfiles: {...entities.locationProfiles},
    parkingLocationProfiles: {...entities.parkingLocationProfiles},
    parkingQuotaProfiles: {...entities.parkingQuotaProfiles},
    users: {...entities.users},
    accessToken,
  };
});

export const setIsSigningIn = createAction('auth/isSigningIn', withPayloadType<boolean>());
export const setIsSigningOut = createAction('auth/isSigningOut', withPayloadType<boolean>());
export const setSignInChecked = createAction('auth/signInChecked', withPayloadType<boolean>());

const cleanupLocalStorage = async (localStore: ILocalStorage) => {
  const allStorageKeys = Object.values(storageKeys.deprecated).concat(Object.values(storageKeys.current));
  await localStore.multiRemove(allStorageKeys);
};

export const signOut = createAsyncThunk<void, {msteams: boolean}, AppThunkApiConfig>(
  'auth/signOut',
  async ({msteams}, {extra}) => {
    const {authManager, localStore} = extra;

    if (!msteams) {
      await authManager.signOut();
    }

    await cleanupLocalStorage(localStore);
  },
);

export const signOutPopup = createAsyncThunk<void, void, AppThunkApiConfig>('auth/signOutPopup', async (_, {extra}) => {
  const {authManager, localStore} = extra;
  await authManager.signOutPopup?.();
  await cleanupLocalStorage(localStore);
});
